import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../api/client";
import Header from "../components/layout/Header";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { useAuth } from "../context/AuthContext";
import { McpProposalActionControl } from "./McpProposalActionControl";
import { EmailAutomationPanel } from "./EmailAutomation";
import { toast } from "sonner";
import {
  AlertTriangle,
  CalendarDays,
  CheckCircle2,
  Clipboard,
  KeyRound,
  Loader2,
  MapPinned,
  PlugZap,
  RefreshCcw,
  ScanSearch,
  Server,
  Settings,
  ShieldCheck,
  Unplug,
} from "lucide-react";

const AREA_LINKS = {
  services: "/cadastros/servicos",
  fixed_evaluations: "/cadastros/avaliacoes-fixas",
  chemicals: "/cadastros/avaliacoes-quimicas",
};

const AREA_LABELS = {
  services: "Serviços e preços",
  fixed_evaluations: "Avaliações fixas",
  chemicals: "Avaliações químicas",
};

const SEVERITY = {
  critical: { label: "Crítico", className: "border-destructive/30 bg-destructive/10 text-destructive" },
  warning: { label: "Atenção", className: "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-400" },
  info: { label: "Revisar", className: "border-border bg-muted/35 text-muted-foreground" },
};

function CopyField({ label, value, testId, mono = true }) {
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(String(value || ""));
      toast.success(`${label} copiado`);
    } catch {
      toast.error("Não foi possível copiar automaticamente");
    }
  };
  return (
    <div className="rounded-xl border border-border bg-background p-3">
      <div className="mb-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="flex items-start gap-2">
        <div data-testid={`${testId}-value`} className={`min-w-0 flex-1 break-all text-sm ${mono ? "font-mono" : ""}`}>{value || "Não configurado"}</div>
        <Button data-testid={`${testId}-copy`} type="button" variant="ghost" size="icon" className="size-8 shrink-0" onClick={copy} disabled={!value} aria-label={`Copiar ${label}`}>
          <Clipboard className="size-4" />
        </Button>
      </div>
    </div>
  );
}

function CodeBlock({ title, value, testId }) {
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(String(value || ""));
      toast.success(`${title} copiado`);
    } catch {
      toast.error("Não foi possível copiar automaticamente");
    }
  };
  return (
    <div className="overflow-hidden rounded-xl border border-border bg-background">
      <div className="flex items-center justify-between gap-3 border-b border-border px-3 py-2">
        <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{title}</div>
        <Button data-testid={`${testId}-copy`} type="button" variant="ghost" size="sm" onClick={copy} disabled={!value}><Clipboard className="size-3.5" /> Copiar</Button>
      </div>
      <pre data-testid={`${testId}-value`} className="max-h-64 overflow-auto whitespace-pre-wrap break-all p-3 text-xs leading-relaxed">{value || "Configuração indisponível"}</pre>
    </div>
  );
}

function McpDiagnosticsPanel() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selftest, setSelftest] = useState(null);
  const [testing, setTesting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/settings/mcp-diagnostics?limit=25");
      setEvents(r.data?.events || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Não foi possível carregar o diagnóstico MCP");
    } finally { setLoading(false); }
  };

  const runSelftest = async () => {
    setTesting(true);
    try {
      const r = await api.post("/settings/mcp-selftest");
      setSelftest(r.data);
      if (r.data?.ok) toast.success("Conexão MCP validada ponta a ponta");
      else toast.error("O autoteste encontrou uma etapa com falha");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao executar o autoteste MCP");
    } finally { setTesting(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  return (
    <div data-testid="mcp-diagnostics-panel" className="rounded-xl border border-border p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="font-medium">Diagnóstico das conexões MCP</div>
          <p className="mt-1 max-w-2xl text-xs leading-relaxed text-muted-foreground">Rode o autoteste para confirmar que o endereço publicado aceita uma conexão autenticada igual à do Claude. Abaixo ficam as últimas tentativas reais — nenhum token, senha ou hash é registrado.</p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="mcp-selftest-run" type="button" onClick={runSelftest} disabled={testing}>
            {testing ? <Loader2 className="size-4 animate-spin" /> : <PlugZap className="size-4" />} Testar conexão agora
          </Button>
            <Button asChild variant="outline"><Link data-testid="mcp-diagnostics-page-link" to="/mcp/diagnostico"><ScanSearch className="size-4" />Log completo</Link></Button>
          <Button data-testid="mcp-diagnostics-refresh" type="button" variant="outline" onClick={load} disabled={loading}>
            {loading ? <Loader2 className="size-4 animate-spin" /> : <ScanSearch className="size-4" />} Atualizar
          </Button>
        </div>
      </div>

      {selftest && (
        <div data-testid="mcp-selftest-result" className={`mt-4 rounded-xl border p-3 ${selftest.ok ? "border-emerald-500/30 bg-emerald-500/[0.05]" : "border-amber-500/30 bg-amber-500/[0.06]"}`}>
          <div className="flex flex-wrap items-center gap-2 text-sm font-medium">
            {selftest.ok ? <CheckCircle2 className="size-4 text-emerald-600" /> : <AlertTriangle className="size-4 text-amber-600" />}
            <span data-testid="mcp-selftest-status">{selftest.ok ? "Conexão MCP funcionando" : "Conexão MCP com falha"}</span>
            <span className="font-mono text-xs text-muted-foreground">{selftest.server_url}</span>
          </div>
          <ul className="mt-2 space-y-1">
            {(selftest.steps || []).map((step, index) => (
              <li key={step.step} data-testid={`mcp-selftest-step-${index}`} className="flex flex-wrap items-center gap-2 text-xs">
                <Badge variant={step.ok ? "secondary" : "outline"}>{step.ok ? "ok" : "falha"}</Badge>
                <span className="font-medium">{step.step}</span>
                {step.status != null && <span className="font-mono text-muted-foreground">HTTP {step.status}</span>}
                <span className="break-all text-muted-foreground">{step.detail}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {events.length === 0 ? (
        <p data-testid="mcp-diagnostics-empty" className="mt-3 text-xs text-muted-foreground">Nenhum evento registrado ainda. Tente conectar o cliente MCP e atualize esta lista.</p>
      ) : (
        <ul data-testid="mcp-diagnostics-list" className="mt-3 space-y-2">
          {events.map((event, index) => (
            <li key={`${event.at}-${index}`} data-testid={`mcp-diagnostics-event-${index}`} className="rounded-lg bg-muted/25 p-3 text-xs">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono font-semibold text-primary">{event.stage}</span>
                <Badge variant={event.outcome === "ok" ? "secondary" : "outline"}>{event.outcome}</Badge>
                <span className="text-muted-foreground">{new Date(event.at).toLocaleString("pt-BR")}</span>
              </div>
              <div className="mt-1 break-all font-mono text-[11px] leading-relaxed text-muted-foreground">
                {Object.entries(event).filter(([k]) => !["at", "stage", "outcome"].includes(k)).map(([k, v]) => `${k}=${v}`).join("  ")}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function Integrations() {  const { isAdmin } = useAuth();
  const [status, setStatus] = useState(null);
  const [mcpInfo, setMcpInfo] = useState(null);
  const [mcpProposalActionsEnabled, setMcpProposalActionsEnabled] = useState(false);
  const [audit, setAudit] = useState(null);
  const [credentials, setCredentials] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const requests = [api.get("/integrations/status")];
      if (isAdmin) {
        requests.push(api.get("/settings/mcp-info"));
        requests.push(api.get("/settings/catalog-audit"));
        requests.push(api.get("/settings"));
      }
      const results = await Promise.allSettled(requests);
      if (results[0].status === "fulfilled") setStatus(results[0].value.data);
      else throw results[0].reason;
      if (isAdmin && results[1]?.status === "fulfilled") setMcpInfo(results[1].value.data);
      if (isAdmin && results[2]?.status === "fulfilled") setAudit(results[2].value.data);
      if (isAdmin && results[3]?.status === "fulfilled") setMcpProposalActionsEnabled(Boolean(results[3].value.data?.proposal?.mcp_proposal_actions_enabled));
      if (isAdmin && results[1]?.status === "rejected") toast.error("Não foi possível carregar os dados de conexão MCP");
      if (isAdmin && results[2]?.status === "rejected") toast.error("Não foi possível executar a auditoria do catálogo");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao carregar integrações");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    const params = new URLSearchParams(window.location.search);
    if (params.get("outlook") === "connected") toast.success("Outlook conectado com sucesso");
    if (["error", "exchange_failed", "invalid_state", "expired_state", "invalid_callback"].includes(params.get("outlook"))) {
      toast.error("Não foi possível concluir a conexão com o Outlook");
    }
  }, [isAdmin]); // eslint-disable-line

  const connectOutlook = async () => {
    setBusy("outlook");
    try {
      const r = await api.get("/integrations/outlook/connect");
      window.location.assign(r.data.authorization_url);
    } catch (e) { toast.error(e.response?.data?.detail || "Falha ao iniciar conexão Microsoft"); setBusy(""); }
  };

  const disconnectOutlook = async () => {
    if (!window.confirm("Desconectar o Outlook deste usuário? Os eventos já criados no calendário não serão apagados.")) return;
    setBusy("outlook");
    try { await api.post("/integrations/outlook/disconnect"); toast.success("Outlook desconectado"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Falha ao desconectar Outlook"); }
    finally { setBusy(""); }
  };

  const geocodeAll = async () => {
    if (!window.confirm("Atualizar a localização dos clientes pelo Google Maps? Apenas endereços cadastrados serão enviados ao serviço de geocodificação.")) return;
    setBusy("google");
    try {
      const r = await api.post("/integrations/google/geocode-all");
      toast.success(`${r.data.resolved} cliente(s) com endereço geocodificado`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Falha ao geocodificar clientes"); }
    finally { setBusy(""); }
  };

  const runAudit = async () => {
    setBusy("audit");
    try {
      const r = await api.get("/settings/catalog-audit");
      setAudit(r.data);
      const problems = Number(r.data?.summary?.critical || 0) + Number(r.data?.summary?.warnings || 0);
      toast.success(problems ? `Auditoria concluída com ${problems} ponto(s) para revisar` : "Auditoria concluída sem alertas relevantes");
    } catch (e) { toast.error(e.response?.data?.detail || "Falha ao auditar catálogo"); }
    finally { setBusy(""); }
  };

  const generateMcpCredentials = async () => {
    setBusy("mcp-token");
    try {
      const r = await api.post("/settings/mcp-generate-credentials");
      setCredentials(r.data);
      toast.success("Novo par de credenciais gerado. Copie o token antes de sair desta tela.");
    } catch (e) { toast.error(e.response?.data?.detail || "Falha ao gerar credencial MCP"); }
    finally { setBusy(""); }
  };

  const mcpClientJson = useMemo(() => mcpInfo?.client_config_template ? JSON.stringify(mcpInfo.client_config_template, null, 2) : "", [mcpInfo]);
  const auditSummary = audit?.summary || {};
  const visibleIssues = (audit?.issues || []).slice(0, 30);

  return (
    <>
      <Header title="Integrações" subtitle="Conexões externas, servidor MCP e diagnóstico do catálogo" actions={<div className="flex gap-2">{isAdmin && <Link to="/configuracoes"><Button variant="outline"><Settings className="size-4" /> Configurar APIs</Button></Link>}<Button variant="outline" onClick={load}><RefreshCcw className="size-4" /> Atualizar</Button></div>} />
      <div className="page-container space-y-6" data-testid="integrations-page">
        {loading && !status ? <div className="h-40 animate-pulse rounded-2xl border bg-card/50" /> : <>
          <div className="grid gap-5 xl:grid-cols-2">
            <Card className="border-border/75"><CardContent className="p-6">
              <div className="flex items-start justify-between gap-4">
                <div className="flex gap-3"><div className="grid size-11 place-items-center rounded-xl bg-primary/10 text-primary"><MapPinned className="size-5" /></div><div><div className="flex items-center gap-2"><h2 className="font-semibold">Google Maps / Routes</h2><Badge variant={status?.google_maps?.configured ? "secondary" : "outline"}>{status?.google_maps?.configured ? "Configurado" : "Sem chave"}</Badge></div><p className="mt-1 text-sm text-muted-foreground">Geocodifica o endereço uma vez e fornece distância e tempo real de carro para a Agenda Inteligente.</p></div></div>
              </div>
              <div className="mt-5 grid grid-cols-2 gap-3"><div className="rounded-xl border p-4"><div className="label-tech">Clientes geocodificados</div><div className="mt-1 font-mono text-2xl font-semibold">{status?.google_maps?.geocoded_clients || 0}</div></div><div className="rounded-xl border p-4"><div className="label-tech">Base total</div><div className="mt-1 font-mono text-2xl font-semibold">{status?.google_maps?.total_clients || 0}</div></div></div>
              {isAdmin && <div className="mt-5 flex flex-wrap gap-2"><Button onClick={geocodeAll} disabled={!status?.google_maps?.configured || busy === "google"}>{busy === "google" ? <Loader2 className="size-4 animate-spin" /> : <MapPinned className="size-4" />} Geocodificar base</Button></div>}
              {!status?.google_maps?.configured && <p className="mt-3 text-xs text-muted-foreground">{isAdmin ? <>Cadastre a chave em <strong>Configurações → APIs e Integrações</strong>. A agenda continua usando bairro/endereço/município até lá.</> : "O Google Maps ainda não foi configurado pelo administrador; o fallback local continua ativo."}</p>}
            </CardContent></Card>

            <Card className="border-border/75"><CardContent className="p-6">
              <div className="flex items-start justify-between gap-4">
                <div className="flex gap-3"><div className="grid size-11 place-items-center rounded-xl bg-primary/10 text-primary"><CalendarDays className="size-5" /></div><div><div className="flex items-center gap-2"><h2 className="font-semibold">Microsoft Outlook</h2><Badge variant={status?.outlook?.connected ? "secondary" : "outline"}>{status?.outlook?.connected ? "Conectado" : status?.outlook?.configured ? "Disponível" : "Não configurado"}</Badge></div><p className="mt-1 text-sm text-muted-foreground">Cria e atualiza eventos das visitas e permite ao CEO consultar sua disponibilidade de calendário.</p></div></div>
              </div>
              {status?.outlook?.connected ? <div className="mt-5 rounded-xl border bg-muted/20 p-4"><div className="flex items-center gap-2 text-sm font-medium"><CheckCircle2 className="size-4 text-primary" /> {status.outlook.display_name || "Conta Microsoft"}</div><div className="mt-1 text-xs text-muted-foreground">{status.outlook.account_email || "Conta autorizada"}</div></div> : null}
              <div className="mt-5 flex flex-wrap gap-2">{status?.outlook?.connected ? <Button variant="outline" onClick={disconnectOutlook} disabled={busy === "outlook"}><Unplug className="size-4" /> Desconectar</Button> : <Button onClick={connectOutlook} disabled={!status?.outlook?.configured || busy === "outlook"}>{busy === "outlook" ? <Loader2 className="size-4 animate-spin" /> : <CalendarDays className="size-4" />} Conectar Outlook</Button>}</div>
              {!status?.outlook?.configured && <p className="mt-3 text-xs text-muted-foreground">{isAdmin ? <>Configure o App Microsoft em <strong>Configurações → APIs e Integrações</strong> e cadastre a mesma Redirect URI no Microsoft Entra.</> : "O App Microsoft ainda não foi configurado pelo administrador."}</p>}
            </CardContent></Card>
          </div>

          {isAdmin && mcpInfo && (
            <Card id="mcp" data-testid="mcp-connection-panel" className="overflow-hidden border-primary/25">
              <CardContent className="p-0">
                <div className="border-b border-border bg-primary/[0.035] p-6">
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                    <div className="flex gap-3">
                      <div className="grid size-11 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary"><Server className="size-5" /></div>
                      <div><div className="flex flex-wrap items-center gap-2"><h2 className="font-semibold">Servidor MCP do CEO</h2><Badge data-testid="mcp-connection-status" variant={mcpInfo.ready ? "secondary" : "outline"}>{mcpInfo.ready ? "Conexão configurada" : mcpInfo.enabled ? "Configuração incompleta" : "Desativado"}</Badge><Badge data-testid="mcp-environment" variant="outline">{mcpInfo.environment}</Badge></div><p className="mt-1 max-w-3xl text-sm leading-relaxed text-muted-foreground">Aqui fica o endereço que você vai copiar para ChatGPT, Claude, VS Code ou qualquer cliente compatível com MCP Streamable HTTP. O CEO nunca mostra o hash configurado nem persiste o token puro.</p></div>
                    </div>
                    <Button data-testid="mcp-generate-credentials" type="button" variant="outline" onClick={generateMcpCredentials} disabled={busy === "mcp-token"}>{busy === "mcp-token" ? <Loader2 className="size-4 animate-spin" /> : <KeyRound className="size-4" />} Gerar token + hash</Button>
                  </div>
                </div>

                <div className="space-y-5 p-6">
                  <div className="grid gap-3 lg:grid-cols-2">
                    <CopyField testId="mcp-server-url" label="Link do servidor MCP" value={mcpInfo.server_url} />
                    <CopyField testId="mcp-health-url" label="Health check do backend" value={mcpInfo.health_url} />
                    <CopyField testId="mcp-oauth-server-discovery" label="Discovery OAuth" value={mcpInfo.oauth_authorization_server} />
                    <CopyField testId="mcp-oauth-resource-discovery" label="Recurso protegido" value={mcpInfo.oauth_protected_resource} />
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                    <div className="rounded-xl border p-3"><div className="label-tech">Transporte</div><div data-testid="mcp-transport" className="mt-1 text-sm font-medium">{mcpInfo.transport}</div></div>
                    <div className="rounded-xl border p-3"><div className="label-tech">Autenticação</div><div data-testid="mcp-authentication" className="mt-1 text-sm font-medium">{mcpInfo.authorization}</div></div>
                    <div className="rounded-xl border p-3"><div className="label-tech">Token no servidor</div><div data-testid="mcp-token-status" className="mt-1 text-sm font-medium">{mcpInfo.token_configured ? "Hash configurado" : "Pendente"}</div></div>
                    <div className="rounded-xl border p-3"><div className="label-tech">Escopos ativos</div><div data-testid="mcp-scopes-count" className="mt-1 text-sm font-medium">{(mcpInfo.scopes || []).length}</div></div>
                  </div>

                  {(mcpInfo.warnings || []).length > 0 && <div className="space-y-2">{mcpInfo.warnings.map((warning, index) => <div data-testid={`mcp-warning-${index}`} key={warning} role="status" className="flex gap-2 rounded-xl border border-amber-500/25 bg-amber-500/[0.06] p-3 text-sm"><AlertTriangle className="mt-0.5 size-4 shrink-0 text-amber-600" /><span>{warning}</span></div>)}</div>}

                  {credentials && <div data-testid="mcp-session-credentials" className="rounded-2xl border border-emerald-500/30 bg-emerald-500/[0.05] p-4">
                    <div className="flex items-start gap-3"><ShieldCheck className="mt-0.5 size-5 shrink-0 text-emerald-600" /><div><div className="font-medium">Credencial gerada nesta sessão</div><p className="mt-1 text-xs leading-relaxed text-muted-foreground">Copie agora. O token puro não é salvo pelo CEO. Coloque o <strong>token puro no cliente MCP</strong> e somente o <strong>hash SHA-256 no ambiente do servidor</strong>.</p></div></div>
                    <div className="mt-4 grid gap-3 lg:grid-cols-2"><CopyField testId="mcp-generated-token" label="TOKEN PURO — cliente MCP" value={credentials.token} /><CopyField testId="mcp-generated-hash" label="MCP_TOKEN_SHA256 — servidor" value={credentials.sha256} /></div>
                  </div>}

                  <div className="grid gap-4 xl:grid-cols-2">
                    <CodeBlock testId="mcp-server-env-template" title="Variáveis para produção (.env)" value={mcpInfo.server_env_template || ""} />
                    <CodeBlock testId="mcp-client-template" title="Modelo de configuração do cliente MCP" value={mcpClientJson} />
                  </div>
                  <p data-testid="mcp-production-config-notice" className="text-xs leading-relaxed text-muted-foreground">Mantenha os segredos JWT_SECRET, SERVER_KEY_BASE64 e MCP_TOKEN_SHA256 existentes. O modo produção também valida CORS e a configuração de backups.</p>

                  <McpProposalActionControl enabled={mcpProposalActionsEnabled} onSaved={load} />

                  <McpDiagnosticsPanel />

                  <div className="rounded-xl border border-border p-4">
                    <div className="font-medium">Escopos e permissões</div>
                    <div className="mt-3 grid gap-2 md:grid-cols-2 xl:grid-cols-3">{Object.entries(mcpInfo.scope_reference || {}).map(([scope, description]) => <div key={scope} className="rounded-lg bg-muted/25 p-3"><div className="font-mono text-xs font-semibold text-primary">{scope}</div><div className="mt-1 text-xs leading-relaxed text-muted-foreground">{description}</div></div>)}</div>
                    <p data-testid="mcp-scope-guidance" className="mt-3 text-xs text-muted-foreground">OAuth mantém as permissões da conta. Para consulta, use read; alterações exigem draft ou actions_write e acesso ao módulo correspondente.</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {isAdmin && <EmailAutomationPanel />}

          {isAdmin && (
            <Card id="catalog-audit" className="border-border/75">
              <CardContent className="p-6">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                  <div className="flex gap-3"><div className="grid size-11 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary"><ScanSearch className="size-5" /></div><div><div className="flex flex-wrap items-center gap-2"><h2 className="font-semibold">Auditoria do catálogo</h2>{audit && <Badge variant={(auditSummary.critical || auditSummary.warnings) ? "outline" : "secondary"}>{auditSummary.critical ? `${auditSummary.critical} crítico(s)` : auditSummary.warnings ? `${auditSummary.warnings} alerta(s)` : "Catálogo consistente"}</Badge>}</div><p className="mt-1 max-w-3xl text-sm leading-relaxed text-muted-foreground">Verifica classificações suspeitas, duplicidades, registros legados e itens que podem entrar no bloco errado da proposta. A auditoria é somente leitura: nenhuma categoria é alterada automaticamente.</p></div></div>
                  <Button type="button" variant="outline" onClick={runAudit} disabled={busy === "audit"}>{busy === "audit" ? <Loader2 className="size-4 animate-spin" /> : <ScanSearch className="size-4" />} Auditar agora</Button>
                </div>

                {audit && <>
                  <div className="mt-5 grid gap-3 sm:grid-cols-3 xl:grid-cols-6">
                    <div className="rounded-xl border p-3"><div className="label-tech">Serviços</div><div className="mt-1 font-mono text-xl font-semibold">{auditSummary.services || 0}</div></div>
                    <div className="rounded-xl border p-3"><div className="label-tech">Avaliações fixas</div><div className="mt-1 font-mono text-xl font-semibold">{auditSummary.fixed_evaluations || 0}</div></div>
                    <div className="rounded-xl border p-3"><div className="label-tech">Químicas</div><div className="mt-1 font-mono text-xl font-semibold">{auditSummary.chemicals || 0}</div></div>
                    <div className="rounded-xl border border-destructive/20 p-3"><div className="label-tech">Críticos</div><div className="mt-1 font-mono text-xl font-semibold text-destructive">{auditSummary.critical || 0}</div></div>
                    <div className="rounded-xl border border-amber-500/20 p-3"><div className="label-tech">Alertas</div><div className="mt-1 font-mono text-xl font-semibold text-amber-600">{auditSummary.warnings || 0}</div></div>
                    <div className="rounded-xl border p-3"><div className="label-tech">Revisões</div><div className="mt-1 font-mono text-xl font-semibold">{auditSummary.info || 0}</div></div>
                  </div>

                  <div className="mt-5 space-y-2">
                    {visibleIssues.length === 0 ? <div className="flex gap-3 rounded-xl border border-emerald-500/25 bg-emerald-500/[0.05] p-4"><CheckCircle2 className="size-5 text-emerald-600" /><div><div className="font-medium">Nenhuma inconsistência encontrada</div><p className="mt-1 text-xs text-muted-foreground">A classificação atual não apresentou os padrões de risco conhecidos pela auditoria.</p></div></div> : visibleIssues.map((issue, index) => {
                      const severity = SEVERITY[issue.severity] || SEVERITY.info;
                      const link = AREA_LINKS[issue.area];
                      return <div key={`${issue.code}-${issue.item_id}-${index}`} className="rounded-xl border border-border p-4">
                        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                          <div className="min-w-0"><div className="flex flex-wrap items-center gap-2"><span className={`rounded-md border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${severity.className}`}>{severity.label}</span><span className="text-xs text-muted-foreground">{AREA_LABELS[issue.area] || issue.area}</span>{issue.suggested_category && <span className="rounded-md bg-primary/10 px-2 py-0.5 text-[10px] text-primary">sugestão: {issue.suggested_category}</span>}</div><div className="mt-2 font-medium">{issue.name}</div><p className="mt-1 text-sm leading-relaxed text-muted-foreground">{issue.message}</p>{issue.suggestion && <p className="mt-1 text-xs leading-relaxed">{issue.suggestion}</p>}</div>
                          {link && <Link to={link}><Button type="button" size="sm" variant="outline">Abrir cadastro</Button></Link>}
                        </div>
                      </div>;
                    })}
                    {(audit?.issues || []).length > visibleIssues.length && <p className="pt-2 text-xs text-muted-foreground">Mostrando os primeiros {visibleIssues.length} de {(audit.issues || []).length} apontamentos.</p>}
                  </div>
                </>}
              </CardContent>
            </Card>
          )}

          <Card><CardContent className="p-5"><div className="font-medium">Privacidade e segurança</div><p className="mt-2 text-sm leading-relaxed text-muted-foreground">Credenciais globais são cadastradas apenas pelo admin. A chave Google nunca vai ao navegador. No Outlook, cada usuário autoriza a própria conta; access/refresh tokens ficam criptografados com AES-256-GCM e podem ser revogados a qualquer momento. No MCP, o servidor guarda apenas o SHA-256 do bearer token; o token puro fica somente no cliente externo autorizado.</p></CardContent></Card>
        </>}
      </div>
    </>
  );
}
